import React from 'react';
import { useNavigate } from 'react-router-dom';

// Generic inline link component for displaying item references
const ItemLink = ({ 
  itemKey, 
  summary, 
  href, 
  colorScheme = 'teal',
  className = "", 
  onClick 
}) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else if (href) {
      // Navigate in the same tab
      navigate(href);
    }
  };

  // Color scheme configurations
  const colorSchemes = {
    teal: {
      bg: 'bg-teal-100/80 dark:bg-teal-800/30',
      hoverBg: 'hover:bg-teal-200/80 dark:hover:bg-teal-700/40',
      border: 'border-teal-200/70 dark:border-teal-600/40',
      hoverBorder: 'hover:border-teal-300 dark:hover:border-teal-500/50',
      keyColor: 'text-teal-600 dark:text-teal-300 group-hover:text-teal-700 dark:group-hover:text-teal-200',
      separator: 'text-teal-400/70 dark:text-teal-500/70',
      summaryColor: 'text-teal-700/90 dark:text-teal-100 group-hover:text-teal-800 dark:group-hover:text-teal-50'
    },
    blue: {
      bg: 'bg-blue-100/80 dark:bg-blue-800/30',
      hoverBg: 'hover:bg-blue-200/80 dark:hover:bg-blue-700/40',
      border: 'border-blue-200/70 dark:border-blue-600/40',
      hoverBorder: 'hover:border-blue-300 dark:hover:border-blue-500/50',
      keyColor: 'text-blue-600 dark:text-blue-300 group-hover:text-blue-700 dark:group-hover:text-blue-200',
      separator: 'text-blue-400/70 dark:text-blue-500/70',
      summaryColor: 'text-blue-700/90 dark:text-blue-100 group-hover:text-blue-800 dark:group-hover:text-blue-50'
    },
    violet: {
      bg: 'bg-violet-100/80 dark:bg-violet-800/30',
      hoverBg: 'hover:bg-violet-200/80 dark:hover:bg-violet-700/40',
      border: 'border-violet-200/70 dark:border-violet-600/40',
      hoverBorder: 'hover:border-violet-300 dark:hover:border-violet-500/50',
      keyColor: 'text-violet-600 dark:text-violet-300 group-hover:text-violet-700 dark:group-hover:text-violet-200',
      separator: 'text-violet-400/70 dark:text-violet-500/70',
      summaryColor: 'text-violet-700/90 dark:text-violet-100 group-hover:text-violet-800 dark:group-hover:text-violet-50'
    }
  };

  const colors = colorSchemes[colorScheme] || colorSchemes.teal;

  return (
    <span 
      onClick={handleClick}
      className={`cursor-pointer group inline-flex items-center ${colors.bg} ${colors.hoverBg} rounded px-1.5 py-0.5 text-xs border ${colors.border} ${colors.hoverBorder} transition-all duration-200 mx-0.5 shadow-sm hover:shadow hover:scale-[1.01] ${className}`}
    >
      <span className={`font-semibold ${colors.keyColor} transition-colors duration-200`}>
        {itemKey}
      </span>
      <span className={`mx-1 ${colors.separator}`}>•</span>
      <span className={`truncate max-w-[180px] ${colors.summaryColor} transition-colors duration-200`}>
        {summary}
      </span>
    </span>
  );
};

// Task Link variant (teal color scheme)
export const TaskLink = ({ taskKey, summary, organizationSlug, className, onClick }) => (
  <ItemLink
    itemKey={taskKey}
    summary={summary}
    href={`/${organizationSlug}/plan/${taskKey}`}
    colorScheme="teal"
    className={className}
    onClick={onClick}
  />
);

// Form Submission Link variant (blue color scheme)
export const FormSubmissionLink = ({ submission, organizationSlug, className, onClick }) => (
  <ItemLink
    itemKey={`#${submission.submissionId ? submission.submissionId.slice(-6) : 'SUB'}`} // Show last 6 characters of ID
    summary={`${submission.formName || 'Form'} submission`}
    href={`/${organizationSlug}/forms/${submission.formSlug}/submissions/${submission.submissionId}`}
    colorScheme="blue"
    className={className}
    onClick={onClick}
  />
);

export default ItemLink; 